import React, { useEffect, useState } from 'react';
import { RingTonePlayer } from 'resources/ringtone-player';
import {
    declineVideoCall,
    answerVideoCall,
    handleVideo2CallDeclined,
    handleVideo2CallAccepted,
} from 'flow/actions/video2-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import UserAvatar from 'flow/components/ui-components/UserAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import useVideo from 'flow/hooks/useVideo';
import { getContactAvatar } from 'flow/utils';
import { addVideoLogEntry } from 'flow/utils/browser-utils';
import { PlayRingtoneSetting } from 'flow/utils/call-utils';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

interface Props {
    videoCallState: any;
    callStatus?: string | null;
    onStatusChange?: (status: string) => void;
    contact?: any;
    contactName?: string;
    contactNumber?: string;
    companyName?: string | null;
    statusText?: string | null;
}

const IncomingVideoCall: React.FC<Props> = ({
    videoCallState,
    callStatus = null,
    onStatusChange,
    contact,
    contactName,
    contactNumber,
    companyName,
    statusText,
}) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [isRinging, setIsRinging] = useState(false);
    const { joinVideoCall } = useVideo();

    const playRingtoneSetting = useAppSelector((state) =>
        PlayRingtoneSetting.get(state.phone.get('playRingtoneSetting')),
    );

    const meetingCode = useAppSelector((state) =>
        state.video2?.get('meetingCode'),
    );

    useEffect(() => {
        const extensionKey = videoCallState?.get('extensionKey');

        if (callStatus && isRinging) {
            RingTonePlayer.stop();
            setIsRinging(false);
            return;
        }

        if (extensionKey) {
            if (
                playRingtoneSetting?.value === PlayRingtoneSetting.RING.value &&
                !isRinging &&
                !callStatus
            ) {
                RingTonePlayer.play();
                setIsRinging(true);
            }
        } else if (isRinging) {
            RingTonePlayer.stop();
            setIsRinging(false);
        }
    }, [videoCallState, playRingtoneSetting, callStatus]);

    useEffect(() => {
        return () => {
            if (isRinging) {
                RingTonePlayer.stop();
                setIsRinging(false);
            }
        };
    }, [isRinging]);

    const onDecline = async () => {
        if (isRinging) {
            RingTonePlayer.stop();
            setIsRinging(false);
        }

        try {
            const callerExtensionKey = videoCallState?.get('extensionKey');

            if (callerExtensionKey) {
                await declineVideoCall(callerExtensionKey);
                addVideoLogEntry(
                    `Declined Video2 call from ${callerExtensionKey}`,
                );

                dispatch(
                    handleVideo2CallDeclined({
                        extensionKey: callerExtensionKey,
                        meetingCode: videoCallState?.get('meetingCode'),
                    }),
                );
            }
        } catch (error) {
            console.error('Error declining video call:', error);
        }

        onStatusChange?.('declined');
    };

    const onAccept = async () => {
        if (isRinging) {
            RingTonePlayer.stop();
            setIsRinging(false);
        }

        try {
            const callerExtensionKey = videoCallState?.get('extensionKey');

            if (callerExtensionKey) {
                await answerVideoCall(callerExtensionKey);
                addVideoLogEntry(
                    `Answered Video2 call from ${callerExtensionKey}`,
                );

                dispatch(
                    handleVideo2CallAccepted({
                        extensionKey: callerExtensionKey,
                    }),
                );
            }
        } catch (error) {
            console.error('Error accepting video call:', error);
        }

        onStatusChange?.('accepted');
    };

    return (
        <div className="no-dark animate-slideInDialpad panel:w-[360px] flex h-full w-screen min-w-[360px] shrink-0 flex-col items-center gap-1 overflow-hidden bg-black p-2">
            <div className="mt-9 flex w-full flex-col items-center justify-center rounded-sm bg-gray-800 text-gray-800">
                <div className="text-yellow my-2 font-bold">
                    {statusText || i18next.t('videocanvas.incoming.video.call')}
                </div>
                <UserAvatar src={getContactAvatar(contact)} size="extraLarge" />
                <div className="mx-3 mt-3 flex flex-col items-center justify-center gap-1.5">
                    <div className="overflow-hidden text-center text-ellipsis whitespace-nowrap text-white">
                        {contactName}
                    </div>
                    {companyName ? (
                        <div className="text-sm/4 text-gray-400">
                            {companyName}
                        </div>
                    ) : null}

                    <div className="text-sm text-gray-400">{contactNumber}</div>
                </div>
                {!statusText && (
                    <div className="my-3 flex h-16 flex-auto items-stretch justify-center gap-5">
                        <div className="flex flex-col items-center gap-2">
                            <IconButton
                                aria-label={i18next.t(
                                    'Call.Canvas.Title.Decline',
                                )}
                                onClick={onDecline}
                                className="bg-red hover:brighten-10 size-10 rounded-xl text-white"
                            >
                                <Icon iconKey="videocam_off" />
                            </IconButton>
                            <span className="text-xs text-white">
                                {i18next.t('Call.Canvas.Title.Decline')}
                            </span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <IconButton
                                aria-label={i18next.t(
                                    'Call.Canvas.Title.Answer',
                                )}
                                onClick={onAccept}
                                className="bg-green hover:brighten-10 size-10 rounded-xl text-white"
                            >
                                <Icon iconKey="video" />
                            </IconButton>
                            <span className="text-xs text-white">
                                {i18next.t('Call.Canvas.Title.Answer')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IncomingVideoCall;
